import BaseScene from '@scenes/base/BaseScene'

import generateItemsToFrames from '@engine/world/penguin/frames/generateItemsToFrames'
import configLoader from '@engine/utils/ConfigLoader'

const interfaceSceneLoaders = {
    Start: () => import(/* webpackChunkName: "scene-start" */ '@scenes/interface/menus/start/Start'),
    Login: () => import(/* webpackChunkName: "scene-login" */ '@scenes/interface/menus/login/Login'),
    Register: () => import(/* webpackChunkName: "scene-register" */ '@scenes/interface/menus/login/register/Register'),
    UsernameChange: () => import(/* webpackChunkName: "scene-username-change" */ '@scenes/interface/menus/login/username_change/UsernameChange'),
    PenguinSelect: () => import(/* webpackChunkName: "scene-penguin-select" */ '@scenes/interface/menus/login/penguin_select/PenguinSelect'),
    PenguinSelector: () => import(/* webpackChunkName: "scene-penguin-selector" */ '@scenes/interface/menus/login/PenguinSelector'),
    PenguinLogin: () => import(/* webpackChunkName: "scene-penguin-login" */ '@scenes/interface/menus/login/penguin_login/PenguinLogin'),
    Servers: () => import(/* webpackChunkName: "scene-servers" */ '@scenes/interface/menus/servers/Servers'),
    Main: () => import(/* webpackChunkName: "scene-main" */ '@scenes/interface/game/main/Main'),
    IglooEdit: () => import(/* webpackChunkName: "scene-igloo-edit" */ '@scenes/interface/game/iglooedit/IglooEdit')
}


export default class Preload extends BaseScene {

    preload() {
        this.load.cacheBuster = TIMESTAMP

        this.load.on('progress', this.onProgress, this)

        WebFont.load(this.crumbs.fonts)

        this.load.pack('preload', 'assets/media/preload/preload-pack.json')
        this.load.pack('stampbook-pack', 'assets/media/interface/game/stampbook/main/stampbook-pack.json')
        
        // Load global UI sound effects (CPI-style)
        this.loadUISounds()
    }
    
    loadUISounds() {
        // Load UI sound effects (Authentic CPI assets)
        const uiSounds = {
            // Button sounds
            'ui-button-click': 'assets/media/sounds/ui/cpi/button-click.ogg',
            'ui-button-action': 'assets/media/sounds/ui/cpi/button-action.ogg',
            'ui-button-close': 'assets/media/sounds/ui/cpi/button-close.ogg',
            'ui-button-tab': 'assets/media/sounds/ui/cpi/tab-click.ogg',
            
            // Interaction/Selection
            'ui-select': 'assets/media/sounds/ui/cpi/select.ogg', // Generic selection
            'ui-button-select': 'assets/media/sounds/ui/cpi/select.ogg', // Legacy mapping
            'ui-impact': 'assets/media/sounds/ui/cpi/impact.ogg',
            
            // Tray/Panel sounds
            'ui-tray-open': 'assets/media/sounds/ui/cpi/tray-open.ogg',
            'ui-tray-close': 'assets/media/sounds/ui/cpi/tray-close.ogg',
            
            // Transitions
            'ui-whoosh-in': 'assets/media/sounds/ui/cpi/whoosh-in.ogg',
            'ui-whoosh-out': 'assets/media/sounds/ui/cpi/whoosh-out.ogg',
            
            // Collection/Economy
            'ui-collect': 'assets/media/sounds/ui/cpi/collect.ogg',
            'ui-coins': 'assets/media/sounds/ui/cpi/coin-loop.ogg',
            
            // Item interactions
            'ui-item-select': 'assets/media/sounds/ui/cpi/item-select.ogg',
            'ui-item-drop': 'assets/media/sounds/ui/cpi/item-drop.ogg',
            
            // Notifications
            'ui-popup': 'assets/media/sounds/ui/cpi/notification.ogg',
            'ui-notification': 'assets/media/sounds/ui/cpi/notification.ogg',

            // Feedback/Fallbacks
            'ui-success': 'assets/media/sounds/ui/cpi/collect.ogg',
            'ui-error': 'assets/media/sounds/ui/cpi/impact.ogg',
            
            // Daily Spin
            'spin': 'assets/media/sounds/spin.ogg',
        }
        
        for (const [key, path] of Object.entries(uiSounds)) {
            // We overwrite to ensure new assets are used if keys conflict
             this.load.audio(key, path)
        }
    }

    async create() {
        try {
            // Try to fetch crumbs from WebAPI first, fall back to bundled crumbs
            let crumbs = await this.loadCrumbs()

            this.game.crumbs = {
                ...this.game.crumbs,
                ...crumbs
            }

            this.game.crumbs.itemsToFrames = generateItemsToFrames(crumbs.secret_frames)

            await this.loadInterfaceScenes()

            // Start
            this.interface.hideLoading()

            this.scene.start('InterfaceController')
            this.scene.start('MemoryManager')
            this.scene.start('Start')

            this.interface.bringToTop()

        } catch (error) {
            console.error('[Preload] Fatal error during initialization:', error)
            this.showMaintenanceScreen(error.message)
        }
    }

    showMaintenanceScreen(errorMessage) {
        // Hide the loading spinner
        this.interface.hideLoading()

        // Start InterfaceController so we can show prompts
        this.scene.start('InterfaceController')
        
        // Wait for InterfaceController to be ready before showing error
        const interfaceScene = this.scene.get('InterfaceController')
        interfaceScene.events.once('create', () => {
            this.interface.bringToTop()
            
            // Show maintenance error with reload option
            const message = 'Unable to connect to the game server.\n\nPlease check your connection and try again.'
            
            this.interface.prompt.showError(message, 'Reload', () => {
                window.location.reload()
            })
        })
    }

    /**
     * Load crumbs from WebAPI (centralized - REQUIRED)
     */
    async loadCrumbs() {
        // Check if WebAPI is enabled
        const config = await configLoader.loadConfig()
        if (!config?.webapi?.enabled || !config?.webapi?.url) {
            console.error('[Preload] WebAPI not configured - crumbs cannot be loaded')
            throw new Error('WebAPI is required but not configured in config.json')
        }

        try {
            console.log('[Preload] Fetching crumbs from WebAPI...')
            const webApiUrl = config.webapi.url.replace(/\/api$/, '') // Remove /api suffix if present
            const response = await fetch(`${webApiUrl}/api/crumbs/bundle/client`, {
                method: 'GET',
                headers: {
                    'Accept': 'application/json',
                    'Cache-Control': 'no-cache',
                    'bypass-tunnel-reminder': 'true' // Bypass localtunnel splash page
                }
            })

            if (!response.ok) {
                throw new Error(`WebAPI responded with ${response.status}`)
            }

            const result = await response.json()
            
            if (!result.data) {
                throw new Error('Invalid crumbs response from WebAPI')
            }

            console.log(`[Preload] Crumbs loaded from WebAPI (version: ${result.version})`)
            
            // Track source for debugging
            this.game.crumbsSource = 'webapi'
            this.game.crumbsVersion = result.version
            
            // Store party/map settings from the bundle metadata
            this.game.activeParty = result.activeParty || null
            this.game.mapStyle = result.mapStyle || 'stadium'
            
            return result.data

        } catch (error) {
            console.error('[Preload] Failed to fetch crumbs from WebAPI:', error.message)
            console.error('[Preload] WebAPI is required - cannot continue without crumbs')
            throw new Error('Failed to load crumbs from WebAPI: ' + error.message)
        }
    }

    async loadInterfaceScenes() {
        try {
            const [
                Start,
                Login,
                Register,
                UsernameChange,
                PenguinSelect,
                PenguinSelector,
                PenguinLogin,
                Servers,
                Main,
                IglooEdit
            ] = await Promise.all([
                interfaceSceneLoaders.Start(),
                interfaceSceneLoaders.Login(),
                interfaceSceneLoaders.Register(),
                interfaceSceneLoaders.UsernameChange(),
                interfaceSceneLoaders.PenguinSelect(),
                interfaceSceneLoaders.PenguinSelector(),
                interfaceSceneLoaders.PenguinLogin(),
                interfaceSceneLoaders.Servers(),
                interfaceSceneLoaders.Main(),
                interfaceSceneLoaders.IglooEdit()
            ])

            this.scene.add('Start', Start.default || Start)
            this.scene.add('Login', Login.default || Login)
            this.scene.add('Register', Register.default || Register)
            this.scene.add('UsernameChange', UsernameChange.default || UsernameChange)
            this.scene.add('PenguinSelect', PenguinSelect.default || PenguinSelect)
            this.scene.add('PenguinSelector', PenguinSelector.default || PenguinSelector)
            this.scene.add('PenguinLogin', PenguinLogin.default || PenguinLogin)
            this.scene.add('Servers', Servers.default || Servers)
            this.scene.add('Main', Main.default || Main)
            this.scene.add('IglooEdit', IglooEdit.default || IglooEdit)

        } catch (error) {
            console.error('[Preload] Failed to load interface scenes', error)
        }
    }

    onProgress(progress) {
        this.interface.loading.progress.scaleX = progress
    }

}
